/**
 * use-member-merge.ts — React hook for the member merge API call.
 *
 * Calls POST /v1/members/merge on betty-core-api.
 * Payload: { retainedMemberId, deprecatedMemberId } — no PHI.
 * Response: 204 No Content on success.
 *
 * PHI constraint: this hook never sends PHI (names, DOB, MBI) in the request body.
 * Only internal ULIDs are transmitted.
 */

import { useState, useCallback } from 'react';

export interface MergeMembersPayload {
  retainedMemberId: string;
  deprecatedMemberId: string;
}

export interface UseMemberMergeReturn {
  merge: (payload: MergeMembersPayload) => Promise<void>;
  isPending: boolean;
  isSuccess: boolean;
  error: Error | null;
}

const MERGE_ENDPOINT = '/v1/members/merge';

export function useMemberMerge(): UseMemberMergeReturn {
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const merge = useCallback(async (payload: MergeMembersPayload): Promise<void> => {
    setIsPending(true);
    setIsSuccess(false);
    setError(null);

    try {
      const response = await fetch(MERGE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          retainedMemberId: payload.retainedMemberId,
          deprecatedMemberId: payload.deprecatedMemberId,
        }),
      });

      if (!response.ok) {
        // Parse RFC 9457 Problem Details if available
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/problem+json')) {
          const problem = (await response.json()) as { title?: string; detail?: string };
          throw new Error(problem.detail ?? problem.title ?? `HTTP ${String(response.status)}`);
        }
        throw new Error(`Merge request failed with status ${String(response.status)}`);
      }

      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsPending(false);
    }
  }, []);

  return { merge, isPending, isSuccess, error };
}
